import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as api from 'api/methods';

import {
  DEFAULT_PRESET_ID,
  getAllEventsOption,
  getChartTypeForPreset,
  getDateRangeFromPeriod,
  getDefaultFilterState,
  getFunnelPresetOptions,
  getPeriodOptions,
  getPlatformOptions,
  isCombinedPaymentsPreset,
  PAYMENTS_SERIES,
  type AnalyticsChartType,
} from './AnalyticsPage.helpers';

import AnalyticsPage from './AnalyticsPage';

type DashboardPreset = {
  id: string;
  name: string;
  description: string;
  config: {
    type: AnalyticsChartType | 'event_count';
    eventName?: string;
    funnelPreset?: string;
    platform?: string;
    retentionEventName?: string;
    combinedPayments?: boolean;
  };
};

type TimeSeriesSeries = {
  id: string;
  label: string;
  color: string;
  data: Array<{ date: string; count: number }>;
};

const DEFAULT_PRESET: DashboardPreset = {
  id: DEFAULT_PRESET_ID,
  name: 'Plugin Activation',
  description: 'Plugin Opened → Login Completed → Task Completed',
  config: {
    type: 'funnel',
    platform: 'Plugin',
    funnelPreset: 'default',
  },
};

const AnalyticsPageContainer = () => {
  const { i18n } = useTranslation();
  const defaultFilters = useMemo(() => getDefaultFilterState(), [i18n.language]);
  const periodOptions = useMemo(() => getPeriodOptions(), [i18n.language]);
  const platformOptions = useMemo(() => getPlatformOptions(), [i18n.language]);
  const funnelPresetOptions = useMemo(() => getFunnelPresetOptions(), [i18n.language]);
  const allEventsOption = useMemo(() => getAllEventsOption(), [i18n.language]);

  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(periodOptions[1]);
  const [platform, setPlatform] = useState(defaultFilters.platform);
  const [eventName, setEventName] = useState(defaultFilters.eventName);
  const [funnelPreset, setFunnelPreset] = useState(defaultFilters.funnelPreset);
  const [retentionEventName, setRetentionEventName] = useState(
    defaultFilters.retentionEventName,
  );
  const [activePresetId, setActivePresetId] = useState(DEFAULT_PRESET_ID);
  const [presets, setPresets] = useState<DashboardPreset[]>([]);
  const [eventNames, setEventNames] = useState<string[]>([]);
  const [timeSeriesSeries, setTimeSeriesSeries] = useState<TimeSeriesSeries[]>([]);
  const [funnelData, setFunnelData] = useState<any[]>([]);
  const [retentionData, setRetentionData] = useState<any>({ cohortSize: 0, data: [] });
  const [events, setEvents] = useState<any[]>([]);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [dashboards, setDashboards] = useState<any[]>([]);

  const chartType = getChartTypeForPreset(activePresetId);
  const isCombinedPayments = isCombinedPaymentsPreset(activePresetId);

  useEffect(() => {
    setPeriod((currentPeriod) =>
      periodOptions.find((option) => option.value === currentPeriod.value) ?? currentPeriod,
    );
    setPlatform((currentPlatform) =>
      platformOptions.find((option) => option.value === currentPlatform.value) ??
      currentPlatform,
    );
    setEventName((currentEventName) =>
      currentEventName.value === ''
        ? allEventsOption
        : currentEventName,
    );
    setFunnelPreset((currentFunnelPreset) =>
      funnelPresetOptions.find((option) => option.value === currentFunnelPreset.value) ??
      currentFunnelPreset,
    );
  }, [allEventsOption, funnelPresetOptions, i18n.language, periodOptions, platformOptions]);

  const applyPreset = useCallback((preset: DashboardPreset) => {
    setActivePresetId(preset.id);
    setEventsPage(1);

    if (preset.config.combinedPayments) {
      setPlatform(platformOptions[0]);
      setEventName(allEventsOption);
      return;
    }

    if (preset.config.platform) {
      const platformOption = platformOptions.find(
        (option) => option.value === preset.config.platform,
      );
      if (platformOption) {
        setPlatform(platformOption);
      }
    } else {
      setPlatform(platformOptions[0]);
    }

    if (preset.config.eventName) {
      setEventName({ label: preset.config.eventName, value: preset.config.eventName });
    } else {
      setEventName(allEventsOption);
    }

    if (preset.config.funnelPreset) {
      const funnelOption = funnelPresetOptions.find(
        (option) => option.value === preset.config.funnelPreset,
      );
      if (funnelOption) {
        setFunnelPreset(funnelOption);
      }
    }

    if (preset.config.retentionEventName) {
      setRetentionEventName(preset.config.retentionEventName);
    } else if (preset.config.type !== 'retention') {
      setRetentionEventName('Signup Completed');
    }
  }, [allEventsOption, funnelPresetOptions, platformOptions]);

  const loadData = useCallback(async () => {
    setLoading(true);

    const { from, to } = getDateRangeFromPeriod(period.value);
    const query = {
      from,
      to,
      ...(platform.value ? { platform: platform.value } : {}),
      ...(eventName.value ? { eventName: eventName.value } : {}),
    };

    try {
      const sharedRequests = [
        api.getAnalyticsPresets(),
        api.getAnalyticsEventNames(),
        api.getAnalyticsEvents({
          ...query,
          page: eventsPage,
          limit: 20,
          sortBy: 'timestamp',
          sortDirection: 'desc',
        }),
        api.getAnalyticsDashboards(),
      ] as const;

      const chartRequests: Promise<unknown>[] = [];

      if (chartType === 'time_series') {
        if (isCombinedPayments) {
          chartRequests.push(
            Promise.all(
              PAYMENTS_SERIES.map((series) =>
                api.getAnalyticsTimeSeries({
                  from,
                  to,
                  period: 'day',
                  eventName: series.eventName,
                  platform: series.platform,
                }),
              ),
            ),
          );
        } else {
          chartRequests.push(
            api.getAnalyticsTimeSeries({ ...query, period: 'day' }),
          );
        }
      }

      if (chartType === 'funnel') {
        chartRequests.push(
          api.getAnalyticsFunnel({ ...query, preset: funnelPreset.value }),
        );
      }

      if (chartType === 'retention') {
        chartRequests.push(
          api.getAnalyticsRetention({ ...query, eventName: retentionEventName }),
        );
      }

      const [sharedResults, ...chartResults] = await Promise.all([
        Promise.all(sharedRequests),
        ...chartRequests,
      ]);

      const [
        presetsResponse,
        namesResponse,
        eventsResponse,
        dashboardsResponse,
      ] = sharedResults;

      setPresets(presetsResponse.data ?? []);
      setEventNames(namesResponse.eventNames ?? []);
      setEvents(eventsResponse.data ?? []);
      setEventsTotalPages(eventsResponse.totalPages ?? 1);
      setDashboards(dashboardsResponse.data ?? []);

      if (chartType === 'time_series') {
        if (isCombinedPayments) {
          const paymentsResponses = chartResults[0] as Array<{ data?: Array<{ date: string; count: number }> }>;
          setTimeSeriesSeries(
            PAYMENTS_SERIES.map((series, index) => ({
              id: series.id,
              label: series.label,
              color: series.color,
              data: paymentsResponses[index]?.data ?? [],
            })),
          );
        } else {
          const timeSeriesResponse = chartResults[0] as { data?: Array<{ date: string; count: number }> };
          setTimeSeriesSeries([
            {
              id: 'events',
              label: eventName.value || 'Events',
              color: '#85E0AB',
              data: timeSeriesResponse.data ?? [],
            },
          ]);
        }
        setFunnelData([]);
        setRetentionData({ cohortSize: 0, data: [] });
      } else if (chartType === 'funnel') {
        const funnelResponse = chartResults[0] as { steps?: any[] };
        setFunnelData(funnelResponse.steps ?? []);
        setTimeSeriesSeries([]);
        setRetentionData({ cohortSize: 0, data: [] });
      } else if (chartType === 'retention') {
        const retentionResponse = chartResults[0] as {
          cohortSize?: number;
          data?: any[];
        };
        setRetentionData(retentionResponse ?? { cohortSize: 0, data: [] });
        setTimeSeriesSeries([]);
        setFunnelData([]);
      }
    } finally {
      setLoading(false);
    }
  }, [
    chartType,
    eventName.value,
    eventsPage,
    funnelPreset.value,
    isCombinedPayments,
    period.value,
    platform.value,
    retentionEventName,
  ]);

  useEffect(() => {
    applyPreset(DEFAULT_PRESET);
  }, [applyPreset]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSaveDashboard = async () => {
    const { from, to } = getDateRangeFromPeriod(period.value);

    await api.createAnalyticsDashboard({
      name: `Overview ${new Date().toLocaleDateString()}`,
      description: 'Saved from Analytics overview',
      config: {
        type: chartType,
        eventName: eventName.value || undefined,
        platform: platform.value || undefined,
        period: 'day',
        from,
        to,
        funnelPreset: funnelPreset.value,
        retentionEventName,
      },
    });

    await loadData();
  };

  const handleDeleteDashboard = async (dashboardId: string) => {
    await api.deleteAnalyticsDashboard(dashboardId);
    await loadData();
  };

  return (
    <AnalyticsPage
      loading={loading}
      presets={presets}
      activePresetId={activePresetId}
      chartType={chartType}
      isCombinedPayments={isCombinedPayments}
      onPresetSelect={applyPreset}
      period={period}
      onPeriodChange={(option: { label: string; value: string }) => {
        setPeriod(option);
        setEventsPage(1);
      }}
      platform={platform}
      onPlatformChange={(option: { label: string; value: string }) => {
        setPlatform(option);
        setEventsPage(1);
      }}
      eventName={eventName}
      onEventNameChange={(option: { label: string; value: string }) => {
        setEventName(option);
        setEventsPage(1);
      }}
      eventNames={eventNames}
      funnelPreset={funnelPreset}
      onFunnelPresetChange={setFunnelPreset}
      timeSeriesSeries={timeSeriesSeries}
      funnelData={funnelData}
      retentionData={retentionData}
      events={events}
      eventsPage={eventsPage}
      eventsTotalPages={eventsTotalPages}
      onEventsNextPage={() => setEventsPage((page) => page + 1)}
      onEventsPrevPage={() => setEventsPage((page) => Math.max(1, page - 1))}
      dashboards={dashboards}
      onSaveDashboard={handleSaveDashboard}
      onDeleteDashboard={handleDeleteDashboard}
      onRefresh={loadData}
    />
  );
};

export default React.memo(AnalyticsPageContainer) as any;
